import { Button, Col, Form, Input, notification, Modal, Row } from "antd";
import BasicButtonPrimary from "components/BasicButtonPrimary";
import { validatorPhone } from "helper/commonValidator";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { List, Image, InputNumber, Space, Typography, Spin } from "antd";
import ProductService from "services/product";
import { useMutation } from "react-query";
import OrderService from "services/order";
import { validatorEmail } from "helper/commonValidator";

const CreateOrder = ({ isModalVisible, onCancel, refetch }) => {
  const [form] = Form.useForm();
  const { t: translation } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedMap, setSelectedMap] = useState({}); // { [productId]: quantity }
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const getProductId = (item) => item?.productId;
  const getProductName = (item) => item?.productName;
  const getProductImage = (item) => item?.productImages || null;
  const getProductPrice = (item) =>
    item?.price ??
    item?.productPrice ??
    item?.unitPrice ??
    item?.sellPrice ??
    0;

  const onClose = () => {
    form.resetFields();
    setProducts([]);
    setSelectedMap({});
    setSearchText("");
    onCancel();
  };

  const createOrderMutation = useMutation({
    mutationFn: (data) => OrderService.createOrder(data),
    onSuccess: () => {
      refetch();
      onClose();
    },
  });

  // debounce search text
  useEffect(() => {
    if (!isModalVisible) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText?.trim() || "");
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText, isModalVisible]);

  // fetch products only when user has searched
  useEffect(() => {
    if (!isModalVisible) return;
    setLoadingProducts(true);
    ProductService.getList({
      ...(debouncedSearch && { searchText: debouncedSearch }),
      order: {
        key: "createdAt",
        value: "desc",
      },
    })
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setProducts(list || []);
      })
      .finally(() => setLoadingProducts(false));
  }, [debouncedSearch, isModalVisible]);

  const selectedCount = useMemo(
    () => Object.keys(selectedMap).length,
    [selectedMap]
  );

  const toggleSelect = (item) => {
    const id = getProductId(item);
    if (!id) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = 1; // default quantity
      }
      return next;
    });
  };

  const changeQty = (item, value) => {
    const id = getProductId(item);
    if (!id) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      const isSelected = !!next[id];
      const val = Number(value || 0);
      if (!isSelected && val > 0) {
        next[id] = Math.max(1, val);
      } else if (isSelected) {
        if (val <= 0 || Number.isNaN(val)) {
          delete next[id];
        } else {
          next[id] = Math.max(1, val);
        }
      }
      return next;
    });
  };

  // Memoize the heavy product list UI to avoid re-render on each keypress
  const productListContent = useMemo(() => {
    return loadingProducts ? (
      <Spin />
    ) : (
      <div className="co-product-scroll">
        <List
          dataSource={products.filter(
            (item) => item?.usageDate && Number(item?.usageDate) > 0
          )}
          locale={{
            emptyText: translation("order.noProducts"),
          }}
          renderItem={(item) => {
            const id = getProductId(item);
            const name = getProductName(item);
            const img = getProductImage(item);
            const price = getProductPrice(item);
            const isSelected = !!selectedMap[id];
            const qty = selectedMap[id] || 0;
            return (
              <List.Item key={id} className="co-product-item">
                <div className="co-product-row">
                  {img ? (
                    <Image
                      src={img}
                      alt={name}
                      width={56}
                      height={56}
                      className="co-product-thumb"
                      preview={false}
                    />
                  ) : (
                    <div className="co-product-thumb co-thumb--placeholder" />
                  )}
                  <div className="co-product-info">
                    <Typography.Text strong ellipsis>
                      {name}
                    </Typography.Text>
                    <div className="co-product-price">
                      {price.toLocaleString("vi-VN")}
                    </div>
                  </div>
                  <div className="co-actions">
                    <Space className="co-qty-group">
                      <Typography.Text>
                        {translation("order.quantity") || "Số lượng"}:
                      </Typography.Text>
                      <InputNumber
                        className="co-qty-input"
                        size="small"
                        min={isSelected ? 1 : 0}
                        step={1}
                        precision={0}
                        onWheel={(e) => e.currentTarget.blur()}
                        value={qty}
                        disabled={!isSelected}
                        onChange={(val) => changeQty(item, val)}
                      />
                    </Space>
                    <Button
                      type={isSelected ? "default" : "primary"}
                      onClick={() => toggleSelect(item)}
                      size="small"
                    >
                      {isSelected
                        ? translation("order.remove")
                        : translation("order.select")}
                    </Button>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </div>
    );
    // Depend only on data that affects the list UI
  }, [loadingProducts, products, selectedMap, translation]);

  return (
    <>
      <Modal
        title={translation("order.createOrder")}
        open={isModalVisible}
        onOk={() => false}
        onCancel={onCancel}
        footer={[
          <Button disabled={createOrderMutation.isPending} onClick={onCancel}>
            {translation("receipt.goBack")}
          </Button>,
          <BasicButtonPrimary
            disabled={createOrderMutation.isPending}
            onclick={() => form.submit()}
            text={translation("order.createOrder")}
          />,
        ]}
        width={720}
      >
        <main>
          <div className="row">
            <div>
              <Form
                layout="vertical"
                form={form}
                onFinish={(values) => {
                  if (!selectedCount) {
                    notification['error']({
                      message: "",
                      description: translation("orderMsg.selectProduct")
                    });
                    return;
                  }
                  const selectedProducts = Object.entries(selectedMap).map(
                    ([productId, orderQuantity]) => ({
                      productId,
                      orderQuantity,
                    })
                  );
                  createOrderMutation.mutate({
                    orderName: values.orderName,
                    userData: {
                      firstName: values.customerName,
                      phoneNumber: values.customerPhone,
                    },
                    orderItemList: selectedProducts,
                  });
                }}
              >
                <Row gutter={16}>
                  <Col md={12} span={24}>
                    <Form.Item
                      name="orderName"
                      label={translation("order.orderName")}
                      rules={[
                        {
                          required: true,
                          message: translation("orderMsg.orderName"),
                        },
                      ]}
                    >
                      <Input autoFocus />
                    </Form.Item>
                  </Col>
                  <Col md={12} span={24}>
                    <Form.Item
                      name="customerName"
                      label={translation("order.customerName")}
                      rules={[
                        {
                          required: true,
                          message: translation("orderMsg.customerName"),
                        },
                      ]}
                    >
                      <Input autoFocus />
                    </Form.Item>
                  </Col>
                  <Col md={12} span={24}>
                    <Form.Item
                      name="customerPhone"
                      label={translation("order.customerPhone")}
                      rules={[
                        {
                          required: true,
                          validator(_, value) {
                            return validatorPhone(value, translation);
                          },
                        },
                      ]}
                    >
                      <Input autoFocus />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Space className="w-100" direction="vertical" size={12}>
                      <Typography.Title level={5}>
                        {translation("order.selectProducts")}
                      </Typography.Title>
                      <Input
                        placeholder={translation("order.searchProducts")}
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                        size="small"
                      />
                      {/* {!debouncedSearch && (
                        <Typography.Text type="secondary">
                          {translation("order.searchToShow")}
                        </Typography.Text>
                      )} */}
                      <Typography.Text type="secondary">
                        {translation("order.selectedCount")?.replace?.(
                          "{count}",
                          selectedCount
                        )}
                      </Typography.Text>
                      {productListContent}
                    </Space>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </main>
      </Modal>
    </>
  );
};

export default CreateOrder;
